import React, { ReactNode } from "react";

import { RcFile } from "antd/es/upload";

import { useTranslation } from "utils";

import { Dragger, Icon, IconButton, Spin, Tag, Typography } from "components";

type UploadFileProps = {
  className?: string;
  id?: string;
  filename?: ReactNode;
  link?: string;
  success?: boolean;
  label?: string;
  subtitle?: string;
  name?: string;
  helpMessage?: string;
  errorMessage?: string;
  /** Advisory block rendered below the drop zone. Unlike `errorMessage`, it keeps the file selected. */
  warning?: ReactNode;
  isWarning?: boolean;
  openFileDialogOnClick?: boolean;
  uploading?: boolean;
  loadingMessage?: string;
  accept?: string;
  beforeUpload?: (file: RcFile) => void;
  onChange?: () => void;
  onDelete?: () => void;
  dataTestId?: string;
};

const formatFilenameForDisplay = (filename: string) => {
  let decoded = filename;

  try {
    decoded = decodeURIComponent(filename);
  } catch {
    decoded = filename;
  }

  return decoded.replace(/_/g, " ");
};

export const UploadFile = ({
  className = "",
  id = "",
  filename,
  link,
  success,
  label,
  subtitle,
  name,
  helpMessage,
  errorMessage,
  warning,
  isWarning,
  openFileDialogOnClick = true,
  uploading = false,
  loadingMessage,
  accept,
  beforeUpload = () => {},
  onChange = () => {},
  onDelete,
}: UploadFileProps) => {
  const { t } = useTranslation("components");

  const stateClass = errorMessage ? (isWarning ? "warning" : "error") : warning ? "warning" : success ? "success" : "";

  const displayFilename = typeof filename === "string" ? formatFilenameForDisplay(filename) : filename;

  return (
    <div className="file-upload">
      <Dragger
        accept={accept}
        action=""
        beforeUpload={beforeUpload}
        className={`${stateClass} ${className}`}
        id={id}
        openFileDialogOnClick={openFileDialogOnClick}
        name={name}
        showUploadList={false}
        onChange={onChange}
      >
        {success ? (
          <>
            <Icon iconName="tabler/file-check" size="xl" />
            <div className="file-upload-text">
              <Typography variant="paragraph-s" className="margin-top-100 margin-bottom-0">
                &ldquo;{displayFilename}&rdquo;
              </Typography>
            </div>

            {(link || onDelete) && (
              <div className="file-actions">
                {link && (
                  <IconButton
                    type="secondary"
                    shape="circle"
                    iconName="tabler/eye"
                    size="small"
                    tooltip={{ title: t("uploadFile.view") }}
                    onClick={(e) => {
                      e.stopPropagation();
                      window.open(link, "_blank", "noopener,noreferrer");
                    }}
                  />
                )}
                {onDelete && (
                  <IconButton
                    type="secondary"
                    shape="circle"
                    iconName="tabler/trash"
                    size="small"
                    tooltip={{ title: t("uploadFile.delete") }}
                    onClick={onDelete}
                  />
                )}
              </div>
            )}
          </>
        ) : uploading ? (
          <>
            <Spin />
            {loadingMessage && (
              <div className="margin-top-50">
                <Typography variant="paragraph-s" color="grey-600">
                  {loadingMessage}
                </Typography>
              </div>
            )}
          </>
        ) : (
          <>
            <Icon iconName="tabler/upload" size="m" />
            <div className="file-upload-text">
              <Typography variant="paragraph-m">{label}</Typography>
              {subtitle && (
                <Typography variant="paragraph-s" color="grey-600">
                  {subtitle}
                </Typography>
              )}
            </div>
            {helpMessage && <Tag className="margin-top-100" colour="green" text={helpMessage} size="small" />}
          </>
        )}
      </Dragger>

      {warning}

      {errorMessage && (
        <Typography variant="paragraph-s" className={`help-message ${isWarning ? "warning" : "error"}`}>
          {errorMessage}
        </Typography>
      )}
    </div>
  );
};
